import React from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import EsqueciSenha from "./pages/EsqueciSenha";
import RedefinirSenha from "./pages/RedefinirSenha";
import Dashboard from "./pages/Dashboard";
import Kanban from "./pages/Kanban";
import OKRs from "./pages/OKRs";
import Empresas from "./pages/Empresas";
import GerenciarEmpresa from "./pages/GerenciarEmpresa";
import MinhaEmpresa from "./pages/MinhaEmpresa";
import Departamentos from "./pages/Departamentos";
import Cargos from "./pages/Cargos";
import CadastroInteligente from "./pages/CadastroInteligente";
import Usuarios from "./pages/Usuarios";
import UsuarioDetalhes from "./pages/UsuarioDetalhes";
import { useAuth } from "@/contexts/AuthContext";
import Relatorios from "./pages/Relatorios";
import GestaoOKRs from "./pages/GestaoOKRs";
import Aprovacoes from "./pages/Aprovacoes";
import AuditLog from "./pages/AuditLog";
import NotificacaoEnvios from "./pages/NotificacaoEnvios";
import ProjetoTPage from "./pages/ProjetoT";
import GerenciarProjetoT from "./pages/GerenciarProjetoT";
import CronogramaPublico from "./pages/CronogramaPublico";
import Organograma from "./pages/Organograma";
import OrganogramaMvp from "./pages/OrganogramaMvp";
import OrganogramaPublico from "./pages/OrganogramaPublico";
import CalendarioPage from "./pages/Calendario";
import Fluxograma from "./pages/Fluxograma";
import FluxogramaEditor from "./pages/FluxogramaEditor";
import FluxogramaPublico from "./pages/FluxogramaPublico";
import Crm from "./pages/Crm";
import CrmNegocioDetalhe from "./pages/CrmNegocioDetalhe";
import CrmContas from "./pages/CrmContas";
import CrmContatos from "./pages/CrmContatos";
import CrmRelatorios from "./pages/CrmRelatorios";
import CrmApiKeys from "./pages/CrmApiKeys";
import Treinamentos from "./pages/Treinamentos";
import Formularios from "./pages/Formularios";
import GerenciarFormulario from "./pages/GerenciarFormulario";
import ResponderFormulario from "./pages/ResponderFormulario";
import FormularioPublico from "./pages/FormularioPublico";
import PoliticaPrivacidade from "./pages/PoliticaPrivacidade";
import GerenciarTreinamentos from "./pages/GerenciarTreinamentos";
import AssistirCurso from "./pages/AssistirCurso";
import EditarCurso from "./pages/EditarCurso";
import Certificado from "./pages/Certificado";
import NotFound from "./pages/NotFound";

// Force HMR cache invalidation - v2
const queryClient = new QueryClient();

function SentryDebugPage() {
  if (!import.meta.env.DEV) {
    return <Navigate to="/login" replace />;
  }

  return (
    <main className="min-h-screen bg-background p-8 text-foreground">
      <button
        className="rounded bg-primary px-4 py-2 text-primary-foreground"
        onClick={() => {
          throw new Error("Sentry frontend test");
        }}
        type="button"
      >
        Disparar erro Sentry
      </button>
    </main>
  );
}

// /perfil e /usuarios/:id foram unificados numa única tela — "meu perfil" é
// só a visão de si mesmo em /usuarios/:id (ver UsuarioDetalhes.tsx).
function PerfilRedirect() {
  const { user } = useAuth();
  if (!user) return null;
  return <Navigate to={`/usuarios/${user.id}`} replace />;
}

// Links antigos (/projetot/:id, /cronogramas/:id — notificações, e-mails, favoritos) apontam pro
// mesmo registro no módulo Projetos.
function RedirecionarParaProjeto() {
  const { id } = useParams();
  return <Navigate to={`/projetos/${id}`} replace />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="theme">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/esqueci-senha" element={<EsqueciSenha />} />
              <Route path="/redefinir-senha" element={<RedefinirSenha />} />
              <Route path="/fp/:token" element={<FormularioPublico />} />
              <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/kanban"
                element={
                  <ProtectedRoute requiredModule="okrs">
                    <Kanban />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/okrs"
                element={
                  <ProtectedRoute requiredModule="okrs">
                    <OKRs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/empresas"
                element={
                  <ProtectedRoute>
                    <Empresas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/empresas/:id"
                element={
                  <ProtectedRoute>
                    <GerenciarEmpresa />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/minha-empresa"
                element={
                  <ProtectedRoute>
                    <MinhaEmpresa />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/minha-empresa/:id"
                element={
                  <ProtectedRoute>
                    <MinhaEmpresa />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/departamentos"
                element={
                  <ProtectedRoute>
                    <Departamentos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cargos"
                element={
                  <ProtectedRoute>
                    <Cargos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cadastro-inteligente"
                element={
                  <ProtectedRoute>
                    <CadastroInteligente />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/usuarios"
                element={
                  <ProtectedRoute>
                    <Usuarios />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/usuarios/:id"
                element={
                  <ProtectedRoute>
                    <UsuarioDetalhes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/perfil"
                element={
                  <ProtectedRoute>
                    <PerfilRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/relatorios"
                element={
                  <ProtectedRoute requiredModule="relatorios">
                    <Relatorios />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/gestao-okrs"
                element={
                  <ProtectedRoute requiredModule="okrs">
                    <GestaoOKRs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aprovacoes"
                element={
                  <ProtectedRoute>
                    <Aprovacoes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/auditoria"
                element={
                  <ProtectedRoute requiredModule="auditoria">
                    <AuditLog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notificacoes-envios"
                element={
                  <ProtectedRoute>
                    <NotificacaoEnvios />
                  </ProtectedRoute>
                }
              />
              {/* Projetos (ex-ProjetoT) substitui desde 2026-09-23 os módulos antigos Projetos e
                  Cronogramas. /projetot e /cronogramas redirecionam pra cá — o id é o mesmo (a
                  migração em backend/sql/projetos_unificacao_dados.sql preserva os ids). */}
              <Route
                path="/projetos"
                element={
                  <ProtectedRoute requiredModule="projetos">
                    <ProjetoTPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projetos/:id"
                element={
                  <ProtectedRoute requiredModule="projetos">
                    <GerenciarProjetoT />
                  </ProtectedRoute>
                }
              />
              <Route path="/projetot" element={<Navigate to="/projetos" replace />} />
              <Route path="/projetot/:id" element={<RedirecionarParaProjeto />} />
              <Route path="/cronogramas" element={<Navigate to="/projetos" replace />} />
              <Route path="/cronogramas/:id" element={<RedirecionarParaProjeto />} />
              <Route
                path="/organograma"
                element={
                  <ProtectedRoute requiredModule="organograma">
                    <Organograma />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organograma/:id"
                element={
                  <ProtectedRoute requiredModule="organograma">
                    <OrganogramaMvp />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendario"
                element={
                  <ProtectedRoute requiredModule="calendario" moduleInAnyEmpresa>
                    <CalendarioPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fluxogramas"
                element={
                  <ProtectedRoute requiredModule="fluxogramas">
                    <Fluxograma />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/fluxogramas/:id"
                element={
                  <ProtectedRoute requiredModule="fluxogramas">
                    <FluxogramaEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/treinamentos"
                element={
                  <ProtectedRoute requiredModule="treinamentos">
                    <Treinamentos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm"
                element={
                  <ProtectedRoute requiredModule="crm">
                    <Crm />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/formularios"
                element={
                  <ProtectedRoute requiredModule="gestao-atividades">
                    <Formularios />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm/negocios/:id"
                element={
                  <ProtectedRoute requiredModule="crm">
                    <CrmNegocioDetalhe />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/formularios/:id"
                element={
                  <ProtectedRoute requiredModule="gestao-atividades">
                    <GerenciarFormulario />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm/contas"
                element={
                  <ProtectedRoute requiredModule="crm">
                    <CrmContas />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm/contatos"
                element={
                  <ProtectedRoute requiredModule="crm">
                    <CrmContatos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm/relatorios"
                element={
                  <ProtectedRoute requiredModule="crm">
                    <CrmRelatorios />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/crm/integracoes"
                element={
                  <ProtectedRoute requiredModule="crm">
                    <CrmApiKeys />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/formularios/:id/responder"
                element={
                  <ProtectedRoute requiredModule="gestao-atividades">
                    <ResponderFormulario />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/treinamentos/gerenciar"
                element={
                  <ProtectedRoute requiredModule="treinamentos">
                    <GerenciarTreinamentos />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/treinamentos/curso/:id/editar"
                element={
                  <ProtectedRoute requiredModule="treinamentos">
                    <EditarCurso />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/treinamentos/curso/:id"
                element={
                  <ProtectedRoute requiredModule="treinamentos">
                    <AssistirCurso />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/treinamentos/curso/:id/certificado"
                element={
                  <ProtectedRoute requiredModule="treinamentos">
                    <Certificado />
                  </ProtectedRoute>
                }
              />
              <Route path="/o/:token" element={<OrganogramaPublico />} />
              <Route path="/c/:token" element={<CronogramaPublico />} />
              <Route path="/f/:token" element={<FluxogramaPublico />} />
              <Route path="/debug-sentry" element={<SentryDebugPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
